import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { ApiError, request } from '../../services/api';
import { type AmazonAccount, sydonAmazonApi } from '../../services/sydonApi';

interface OrgSettings {
  legal_name: string;
  email: string;
  address_line1: string;
  city: string;
  state: string;
  zip: string;
}

interface NotificationSettings {
  alert_email: string;
  notify_at_80: boolean;
  notify_at_90: boolean;
  notify_at_100: boolean;
  [key: string]: string | boolean;
}

interface StacSettingsState {
  org: OrgSettings;
  filing_frequencies: Record<string, string>;
  notifications: NotificationSettings;
}

const DEFAULT_SETTINGS: StacSettingsState = {
  org: { legal_name: '', email: '', address_line1: '', city: '', state: '', zip: '' },
  filing_frequencies: {},
  notifications: { alert_email: '', notify_at_80: true, notify_at_90: true, notify_at_100: true },
};

const FREQ_OPTIONS = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annual', label: 'Annual' },
];

const LABEL: React.CSSProperties = {
  display: 'block',
  fontSize: 11,
  fontWeight: 700,
  color: '#64748B',
  textTransform: 'uppercase',
  letterSpacing: '.05em',
  marginBottom: 6,
};
const INPUT: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  border: '1px solid #D1D5DB',
  borderRadius: 8,
  fontSize: 13,
  color: '#0F172A',
  background: '#fff',
  boxSizing: 'border-box',
  outline: 'none',
};
const TH: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  color: '#64748B',
  textTransform: 'uppercase',
  letterSpacing: '.05em',
};

function SectionCard({
  title,
  children,
}: { title: string; children: React.ReactNode }): JSX.Element {
  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #E2E8F0',
        borderRadius: 12,
        marginBottom: 16,
        overflow: 'hidden',
      }}
    >
      <div style={{ padding: '14px 20px', borderBottom: '1px solid #E2E8F0' }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: '#0F172A' }}>{title}</span>
      </div>
      <div style={{ padding: 20 }}>{children}</div>
    </div>
  );
}

function Sk({ w, h }: { w?: number | string; h?: number }): JSX.Element {
  return (
    <div
      className="skeleton-box"
      style={{ width: w ?? '100%', height: h ?? 36, borderRadius: 8 }}
    />
  );
}

function SkSection({ count }: { count: number }): JSX.Element {
  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #E2E8F0',
        borderRadius: 12,
        marginBottom: 16,
        overflow: 'hidden',
      }}
    >
      <div style={{ padding: '14px 20px', borderBottom: '1px solid #E2E8F0' }}>
        <Sk w={150} h={14} />
      </div>
      <div style={{ padding: 20 }}>
        {Array.from({ length: count }).map((_, i) => (
          <div key={i} style={{ marginBottom: i < count - 1 ? 18 : 0 }}>
            <div
              className="skeleton-box"
              style={{ width: 130, height: 11, borderRadius: 8, marginBottom: 6 }}
            />
            <Sk h={36} />
          </div>
        ))}
      </div>
    </div>
  );
}

function AmazonConnectionCard(): JSX.Element {
  const { accessToken } = useAuth();
  const [account, setAccount] = useState<AmazonAccount | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [connecting, setConnecting] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);

  const load = useCallback((): void => {
    setLoading(true);
    setLoadError(null);
    sydonAmazonApi
      .getAccounts(accessToken)
      .then((accounts) => setAccount(accounts[0] ?? null))
      .catch((e: Error) => setLoadError(e.message))
      .finally(() => setLoading(false));
  }, [accessToken]);

  useEffect(load, [load]);

  function handleConnect(): void {
    // Opened synchronously so the browser still treats this as a user-gesture
    // popup once authUrl arrives — a window.open() inside the .then() below
    // would get blocked by most popup blockers.
    const popup = window.open('', '_blank');
    setConnecting(true);
    setConnectError(null);
    sydonAmazonApi
      .getAuthUrl(accessToken)
      .then(({ authUrl }) => {
        if (popup) popup.location.href = authUrl;
        else window.open(authUrl, '_blank');
      })
      .catch((e: Error) => {
        popup?.close();
        setConnectError(e.message);
      })
      .finally(() => setConnecting(false));
  }

  return (
    <SectionCard title="Amazon Connection">
      {loading ? (
        <Sk h={36} />
      ) : loadError ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 13, color: '#BE123C', flex: 1 }}>
            Could not load your Amazon connection: {loadError}
          </span>
          <button
            type="button"
            onClick={load}
            style={{
              padding: '6px 14px',
              background: '#0F172A',
              color: '#fff',
              border: 'none',
              borderRadius: 7,
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Retry
          </button>
        </div>
      ) : account ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: '#15803D',
              background: '#F0FDF4',
              border: '1px solid #BBF7D0',
              borderRadius: 6,
              padding: '4px 10px',
            }}
          >
            Connected
          </span>
          <span style={{ fontSize: 13, color: '#374151' }}>
            Seller ID {account.seller_id} · Marketplace {account.marketplace_id}
          </span>
        </div>
      ) : (
        <div>
          <div style={{ fontSize: 13, color: '#64748B', marginBottom: 12 }}>
            Connect your Amazon seller account to pull order data for nexus and taxability.
          </div>
          <button
            type="button"
            onClick={handleConnect}
            disabled={connecting}
            style={{
              padding: '9px 20px',
              background: connecting ? '#94A3B8' : '#1B4F8C',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 700,
              cursor: connecting ? 'not-allowed' : 'pointer',
            }}
          >
            {connecting ? 'Opening…' : 'Connect Amazon Account'}
          </button>
          {connectError && (
            <div style={{ fontSize: 12, color: '#BE123C', marginTop: 8 }}>
              Could not start the Amazon connect flow: {connectError}
            </div>
          )}
        </div>
      )}
    </SectionCard>
  );
}

export default function StacSettings(): JSX.Element {
  const { organizationId } = useAuth();
  const [settings, setSettings] = useState<StacSettingsState>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [loadErrStatus, setLoadErrStatus] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const load = useCallback((): void => {
    setLoading(true);
    setLoadErr(null);
    setLoadErrStatus(null);
    request<StacSettingsState>(`/api/stac/settings?organizationId=${organizationId}`)
      .then((data) => setSettings({ ...DEFAULT_SETTINGS, ...data }))
      .catch((e: Error) => {
        setLoadErr(e.message);
        setLoadErrStatus(e instanceof ApiError ? e.status : null);
      })
      .finally(() => setLoading(false));
  }, [organizationId]);

  useEffect(load, [load]);

  function patch<K extends keyof StacSettingsState, F extends keyof StacSettingsState[K]>(
    section: K,
    key: F,
    val: StacSettingsState[K][F],
  ): void {
    setSettings((s) => ({ ...s, [section]: { ...s[section], [key]: val } }));
  }

  function handleSave(): void {
    setSaving(true);
    setSaveMsg(null);
    request<void>('/api/stac/settings', {
      method: 'PUT',
      body: JSON.stringify({ organization_id: organizationId, ...settings }),
    })
      .then(() => setSaveMsg({ ok: true, text: 'Settings saved.' }))
      .catch((e: Error) => setSaveMsg({ ok: false, text: e.message }))
      .finally(() => setSaving(false));
  }

  if (loadErr && loadErrStatus === 403)
    return (
      <div style={{ padding: 24 }}>
        <div
          style={{
            background: '#FFF1F2',
            border: '1px solid #FECDD3',
            borderRadius: 10,
            padding: '16px 20px',
          }}
        >
          <span style={{ fontSize: 13, color: '#BE123C' }}>
            You don't have permission to view this — ask your organization admin for access.
          </span>
        </div>
      </div>
    );

  if (loadErr)
    return (
      <div style={{ padding: 24 }}>
        <div
          style={{
            background: '#FFF1F2',
            border: '1px solid #FECDD3',
            borderRadius: 10,
            padding: '16px 20px',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <span style={{ fontSize: 13, color: '#BE123C', flex: 1 }}>{loadErr}</span>
          <button
            onClick={load}
            style={{
              padding: '6px 14px',
              background: '#0F172A',
              color: '#fff',
              border: 'none',
              borderRadius: 7,
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );

  const freqEntries = Object.entries(settings.filing_frequencies);

  return (
    <div
      style={{
        padding: '18px 22px',
        background: '#F8FAFC',
        minHeight: '100%',
        boxSizing: 'border-box',
      }}
    >
      <div style={{ maxWidth: 740 }}>
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: '#0F172A', marginBottom: 2 }}>
            Tax Settings
          </div>
          <div style={{ fontSize: 13, color: '#64748B' }}>
            Organization profile, filing preferences, and alert notifications.
          </div>
        </div>

        {loading ? (
          <>
            <SkSection count={4} />
            <SkSection count={3} />
            <SkSection count={3} />
          </>
        ) : (
          <>
            <AmazonConnectionCard />

            <SectionCard title="Organization Profile">
              <div style={{ marginBottom: 18 }}>
                <div style={LABEL}>Legal Name</div>
                <input
                  type="text"
                  value={settings.org.legal_name}
                  onChange={(e) => patch('org', 'legal_name', e.target.value)}
                  placeholder="Acme Commerce LLC"
                  style={INPUT}
                />
              </div>
              <div style={{ marginBottom: 18 }}>
                <div style={LABEL}>Billing Email</div>
                <input
                  type="email"
                  value={settings.org.email}
                  onChange={(e) => patch('org', 'email', e.target.value)}
                  placeholder="tax@example.com"
                  style={INPUT}
                />
              </div>
              <div style={{ marginBottom: 18 }}>
                <div style={LABEL}>Street Address</div>
                <input
                  type="text"
                  value={settings.org.address_line1}
                  onChange={(e) => patch('org', 'address_line1', e.target.value)}
                  placeholder="123 Commerce St"
                  style={INPUT}
                />
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 2 }}>
                  <div style={LABEL}>City</div>
                  <input
                    type="text"
                    value={settings.org.city}
                    onChange={(e) => patch('org', 'city', e.target.value)}
                    placeholder="Seattle"
                    style={INPUT}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={LABEL}>State</div>
                  <input
                    type="text"
                    value={settings.org.state}
                    onChange={(e) => patch('org', 'state', e.target.value.toUpperCase())}
                    placeholder="WA"
                    maxLength={2}
                    style={INPUT}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={LABEL}>ZIP</div>
                  <input
                    type="text"
                    value={settings.org.zip}
                    onChange={(e) => patch('org', 'zip', e.target.value)}
                    placeholder="98101"
                    maxLength={10}
                    style={INPUT}
                  />
                </div>
              </div>
            </SectionCard>

            <SectionCard title="Filing Frequency by State">
              {freqEntries.length === 0 ? (
                <div
                  style={{ padding: '12px 0', textAlign: 'center', fontSize: 13, color: '#94A3B8' }}
                >
                  No states configured.{' '}
                  <span style={{ color: '#1B4F8C', fontWeight: 600 }}>
                    Monitor thresholds in Nexus Status to add registrations.
                  </span>
                </div>
              ) : (
                <div style={{ border: '1px solid #E2E8F0', borderRadius: 8, overflow: 'hidden' }}>
                  <div
                    style={{
                      display: 'flex',
                      background: '#F8FAFC',
                      padding: '8px 14px',
                      borderBottom: '1px solid #E2E8F0',
                    }}
                  >
                    <span style={{ flex: '0 0 200px', ...TH }}>State</span>
                    <span style={{ flex: 1, ...TH }}>Frequency</span>
                  </div>
                  {freqEntries.map(([code, freq], i) => (
                    <div
                      key={code}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '10px 14px',
                        borderBottom: i < freqEntries.length - 1 ? '1px solid #F1F5F9' : 'none',
                        background: '#fff',
                      }}
                    >
                      <span
                        style={{
                          flex: '0 0 200px',
                          fontSize: 13,
                          fontWeight: 600,
                          color: '#0F172A',
                        }}
                      >
                        {code}
                      </span>
                      <select
                        value={freq}
                        onChange={(e) => patch('filing_frequencies', code, e.target.value)}
                        style={{ ...INPUT, width: 160 }}
                      >
                        {FREQ_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
              )}
            </SectionCard>

            <SectionCard title="Notifications">
              <div style={{ marginBottom: 20 }}>
                <div style={LABEL}>Alert Email</div>
                <input
                  type="email"
                  value={settings.notifications.alert_email}
                  onChange={(e) => patch('notifications', 'alert_email', e.target.value)}
                  placeholder="alerts@example.com"
                  style={INPUT}
                />
              </div>
              <div style={LABEL}>Nexus Threshold Alerts</div>
              {(
                [
                  ['notify_at_80', 'Notify when 80% of a state threshold is reached'],
                  ['notify_at_90', 'Notify when 90% of a state threshold is reached'],
                  ['notify_at_100', 'Notify when a nexus threshold is crossed'],
                ] as [keyof NotificationSettings, string][]
              ).map(([key, label]) => (
                <label
                  key={String(key)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    marginBottom: 8,
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={!!settings.notifications[key]}
                    onChange={(e) => patch('notifications', key, e.target.checked)}
                    style={{
                      width: 15,
                      height: 15,
                      accentColor: '#1B4F8C',
                      cursor: 'pointer',
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ fontSize: 13, color: '#374151' }}>{label}</span>
                </label>
              ))}
            </SectionCard>

            {saveMsg && (
              <div
                style={{
                  background: saveMsg.ok ? '#F0FDF4' : '#FFF1F2',
                  border: `1px solid ${saveMsg.ok ? '#BBF7D0' : '#FECDD3'}`,
                  borderRadius: 8,
                  padding: '10px 16px',
                  marginBottom: 14,
                  fontSize: 13,
                  fontWeight: 500,
                  color: saveMsg.ok ? '#15803D' : '#BE123C',
                }}
              >
                {saveMsg.text}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', paddingBottom: 24 }}>
              <button
                onClick={handleSave}
                disabled={saving}
                style={{
                  padding: '9px 24px',
                  background: saving ? '#94A3B8' : '#1B4F8C',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 700,
                  cursor: saving ? 'not-allowed' : 'pointer',
                  letterSpacing: '.02em',
                  transition: 'background 0.15s',
                }}
              >
                {saving ? 'Saving…' : 'Save Settings'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
